import { useConversationSidePanelContext } from "@app/components/assistant/conversation/ConversationSidePanelContext";
import { ConversationSidePanelHeader } from "@app/components/assistant/conversation/ConversationSidePanelHeader";
import { CenteredState } from "@app/components/assistant/conversation/interactive_content/CenteredState";
import {
  FilePreviewContent,
  useFilePreviewContent,
} from "@app/components/file_explorer/FilePreviewContent";
import type { FileEntry } from "@app/components/file_explorer/types";
import { useConversationAttachments } from "@app/hooks/conversations/useConversationAttachments";
import { useConversationSandboxFiles } from "@app/hooks/conversations/useConversationSandboxFiles";
import { isFileAttachmentType } from "@app/lib/api/assistant/conversation/attachments";
import { getFileTypeIcon } from "@app/lib/file_icon_utils";
import {
  getFileDownloadUrl,
  getFilePathDownloadUrl,
  getFilePathViewUrl,
  getFileViewUrl,
  useFileMetadata,
} from "@app/lib/swr/files";
import type { ConversationAttachmentType } from "@app/types/api/assistant/conversation/attachments";
import type {
  FileSystemEntry,
  FileSystemFileEntry,
} from "@app/types/api/file_system/types";
import type { ConversationWithoutContentType } from "@app/types/assistant/conversation";
import type { FilePreviewTarget } from "@app/types/conversation_side_panel";
import { parseFilePreviewTarget } from "@app/types/conversation_side_panel";
import type { FileTypeWithMetadata } from "@app/types/files";
import { contentTypeFromFileName } from "@app/types/files";
import type { LightWorkspaceType } from "@app/types/user";
import { Button, Download01, Icon, Spinner } from "@dust-tt/sparkle";

interface ResolvedPreviewFile {
  entry: FileEntry;
  viewUrl: string;
  downloadUrl: string;
}

/**
 * Both identifiers resolve against the listing that owns them: a mount path against the
 * conversation filesystem, a file id against the attachments (where agent-generated files
 * with no mount path live).
 */
function resolvePreviewFile({
  attachments,
  fileMetadata,
  owner,
  sandboxFiles,
  target,
}: {
  attachments: ConversationAttachmentType[];
  fileMetadata: FileTypeWithMetadata | undefined;
  owner: LightWorkspaceType;
  sandboxFiles: FileSystemEntry[];
  target: FilePreviewTarget | null;
}): ResolvedPreviewFile | null {
  if (!target) {
    return null;
  }

  if (target.kind === "path") {
    const { filePath } = target;
    const fileName = filePath.split("/").pop() ?? filePath;
    // Prefer the listing entry once it has loaded, for the real content type, file id and
    // version. Until then derive what we can from the file name: Office documents have no
    // in-browser renderer, so the content type picks the preview strategy and the icon.
    const sandboxFile = sandboxFiles.find(
      (f): f is FileSystemFileEntry => !f.isDirectory && f.path === filePath
    );

    return {
      entry: sandboxFile
        ? { ...sandboxFile, kind: "file" }
        : {
            kind: "file",
            isDirectory: false,
            fileName,
            path: filePath,
            contentType: contentTypeFromFileName(fileName) ?? "",
            fileId: null,
            thumbnailUrl: null,
            sizeBytes: 0,
            lastModifiedMs: 0,
          },
      viewUrl: getFilePathViewUrl(owner, filePath),
      downloadUrl: getFilePathDownloadUrl(owner, filePath),
    };
  }

  const { fileId } = target;
  const attachment = attachments
    .filter(isFileAttachmentType)
    .find((a) => a.fileId === fileId);
  // A chat shows several conversations, so a file from another one is found by its id.
  const fileFromMetadata =
    !attachment && fileMetadata?.sId === fileId
      ? {
          title: fileMetadata.fileName,
          path: fileMetadata.fileName,
          contentType: fileMetadata.contentType,
          updatedAt: fileMetadata.version,
        }
      : null;
  const file = attachment
    ? {
        title: attachment.title,
        path: attachment.path ?? attachment.title,
        contentType: attachment.contentType,
        updatedAt: attachment.updatedAt ?? 0,
      }
    : fileFromMetadata;
  if (!file) {
    return null;
  }

  return {
    entry: {
      kind: "file",
      isDirectory: false,
      fileName: file.title,
      path: file.path,
      contentType: file.contentType,
      fileId,
      thumbnailUrl: null,
      sizeBytes: 0,
      lastModifiedMs: file.updatedAt,
    },
    viewUrl: getFileViewUrl(owner, fileId),
    downloadUrl: getFileDownloadUrl(owner, fileId),
  };
}

interface FilePreviewPanelProps {
  conversation: ConversationWithoutContentType;
  owner: LightWorkspaceType;
}

/**
 * The one preview surface for conversation files, whichever type they are and whether they
 * are known by mount path or by file id.
 */
export function FilePreviewPanel({
  conversation,
  owner,
}: FilePreviewPanelProps) {
  const { currentPanel, data, closePanel, openPanel, backToDetails } =
    useConversationSidePanelContext();
  const target = parseFilePreviewTarget(data, currentPanel);

  // The conversion preview is cached (Cache-Control: max-age) per URL, so we
  // bust it with the file's lastModifiedMs. SWR revalidates this list on mount
  // and window focus, so the preview refreshes after the file is touched (and
  // the reload button forces an immediate revalidation).
  // TODO: use E2B events of "files are updated", when they are merged
  const { sandboxFiles, isSandboxFilesLoading } = useConversationSandboxFiles({
    conversationId: conversation.sId,
    owner,
    options: { disabled: target?.kind !== "path" },
  });

  const { attachments, isConversationAttachmentsLoading } =
    useConversationAttachments({
      conversationId: conversation.sId,
      owner,
      options: { disabled: target?.kind !== "fileId" },
    });

  const isAttachmentMissing =
    target?.kind === "fileId" &&
    !isConversationAttachmentsLoading &&
    !attachments.some(
      (a) => isFileAttachmentType(a) && a.fileId === target.fileId
    );
  const { fileMetadata, isFileMetadataLoading } = useFileMetadata({
    fileId: isAttachmentMissing ? target.fileId : null,
    owner,
  });

  const resolved = resolvePreviewFile({
    attachments,
    fileMetadata,
    owner,
    sandboxFiles,
    target,
  });

  const {
    category,
    truncatedContent,
    processedContent,
    hasError,
    isContentLoading,
  } = useFilePreviewContent({
    entry: resolved?.entry ?? null,
    fileUrl: resolved?.viewUrl ?? null,
    enabled: !!resolved,
  });

  if (!target) {
    return null;
  }

  const isMetadataLoading =
    !resolved &&
    (target.kind === "fileId"
      ? isConversationAttachmentsLoading ||
        (isAttachmentMissing && isFileMetadataLoading)
      : isSandboxFilesLoading);

  const fileName = resolved?.entry.fileName ?? "";
  const FileIcon = getFileTypeIcon(resolved?.entry.contentType ?? "", fileName);

  return (
    <div className="sheet flex h-panel min-h-0 flex-col">
      <ConversationSidePanelHeader
        backTooltip={backToDetails ? "Back to In this chat" : "Back to files"}
        onBack={backToDetails ?? (() => openPanel({ type: "files" }))}
        onClose={closePanel}
      >
        <div className="flex min-w-0 items-center gap-1.5">
          <Icon visual={FileIcon} size="sm" className="shrink-0" />
          <span className="line-clamp-1 text-sm font-medium">{fileName}</span>
        </div>
        {resolved && (
          <div className="ml-2 flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              icon={Download01}
              tooltip="Download"
              href={resolved.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
            />
          </div>
        )}
      </ConversationSidePanelHeader>
      <div
        className={
          category === "html_artifact"
            ? "min-h-0 flex-1 overflow-hidden bg-muted-background p-0"
            : "min-h-0 flex-1 overflow-y-auto bg-muted-background p-4"
        }
      >
        {isMetadataLoading ? (
          <CenteredState>
            <Spinner />
          </CenteredState>
        ) : resolved && !hasError ? (
          <FilePreviewContent
            category={category}
            entry={resolved.entry}
            fileContent={truncatedContent}
            fileUrl={resolved.viewUrl}
            isContentLoading={isContentLoading}
            isFullWidth
            markdownContent={processedContent?.text}
            markdownViewMode="preview"
            owner={owner}
            processedContent={processedContent}
          />
        ) : (
          <CenteredState>
            <p className="text-sm text-muted-foreground">
              Unable to preview this file. You can download it instead.
            </p>
          </CenteredState>
        )}
      </div>
    </div>
  );
}
